<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { getPermissionsAction } from '@/api/functions/access/get-permissions-action'
import { getRolesAction } from '@/api/functions/access/get-roles-action'
import { deleteRoleAction } from '@/api/functions/access/roles-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import AccessOverview from './partials/AccessOverview.vue'
import CreateRoleModal from './partials/CreateRoleModal.vue'
import UpdateRoleModal from './partials/UpdateRoleModal.vue'

const toast = useToast()
const queryClient = useQueryClient()

const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)

const selectedRole = ref()

const tabs = [
  { title: 'اعضا', link: '/access' },
  { title: 'نقش‌ها', link: '/access/roles' },
  { title: 'مجوزها', link: '/access/permissions' },
]

const { data: rolesData, refetch: refetchRoles } = useQueryApi({
  queryKey: ['roles'],
  queryFn: () => getRolesAction(),
  staleTime: 1000 * 60 * 3,
})

const { data: permissionsData } = useQueryApi({
  queryKey: ['permissions'],
  queryFn: () => getPermissionsAction(),
  staleTime: 1000 * 60 * 3,
})

const columns = [
  { key: 'roleName', label: 'نام نقش', class: 'col-span-3 text-sm' },
  { key: 'permissions', label: 'مجوزها', class: 'col-span-5 text-sm' },
]

const actions = [
  { label: 'ویرایش نقش', handlerName: 'editRole' },
  { label: 'حذف نقش', handlerName: 'deleteRole' },
]

const roles = computed(() => {
  const list = rolesData.value?.data || []
  return list.map((r) => {
    const isSuperAdmin = r.superAdmin
    return {
      roleName: r.name,
      permissions: isSuperAdmin ? 'تمام مجوزها' : r.permissions?.map((p) => p.label).join('، ') || '-',
      raw: r,
    }
  })
})

const permissionsOptions = computed(() => {
  const permissions = permissionsData.value?.data || []
  return permissions.map((p) => ({
    label: p.label,
    value: p.id,
  }))
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditRole = (role: any) => {
  selectedRole.value = role.raw
  showUpdateModal.value = true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteRole = (role: any) => {
  selectedRole.value = role.raw
  showDeleteModal.value = true
}

const deleteMutation = useMutationApi({
  mutationFn: (id: number) => deleteRoleAction(id),
  onSuccess: () => {
    toast.success('نقش با موفقیت حذف شد')
    showDeleteModal.value = false
    selectedRole.value = null
    queryClient.invalidateQueries({ queryKey: ['roles'] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteRole = (error: Error) => {
  if (!selectedRole.value?.id) {
    toast.error(getErrorMessage(error), 'خطا')
    return
  }
  deleteMutation.mutate(selectedRole.value.id)
}
</script>

<template>
  <div class="w-full mb-12">
    <ConfirmModal v-model="showDeleteModal" title="حذف نقش" :message="'آیا از حذف این نقش اطمینان دارید؟'"
      confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteRole" @cancel="showDeleteModal = false" />

    <UpdateRoleModal v-model="showUpdateModal" :selectedRole="selectedRole" :permissionsOptions="permissionsOptions"
      @confirm="refetchRoles" @cancel="showUpdateModal = false" />

    <CreateRoleModal v-model="showCreateModal" :permissionsOptions="permissionsOptions" @confirm="refetchRoles"
      @cancel="showCreateModal = false" />


    <AccessOverview :tabs="tabs">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">
          <Button :isAdmin="true" @click="showCreateModal = true"
            class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
            افزودن نقش جدید
          </Button>
        </div>
      </template>
    </AccessOverview>

    <div class="mt-20 lg:mt-8">
      <Table :columns="columns" :rows="roles" :actions="actions" @primaryAction="onEditRole"
        @secondaryAction="onDeleteRole" />
    </div>
  </div>
</template>
